import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// REACT_APP_BACKEND_URL, from one of two places:
// - Azure: window.APP_CONFIG, which server.js fills from the Web App setting at
//   runtime, so one build works in any environment.
// - Local dev: config.js is empty, so the value from src/frontend/.env,
//   inlined at build time, applies.
const BACKEND_URL =
  window.APP_CONFIG?.backendUrl || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8123';
const API_BASE_URL = `${BACKEND_URL}/api`;

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const tokenStorage = {
  getAccess: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  set: (access?: string, refresh?: string) => {
    if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

export const isAuthenticated = (): boolean => !!tokenStorage.getAccess();

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccess();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Endpoints where a 401 means "bad credentials", not "expired session".
const AUTH_PATHS = [
  '/auth/login/',
  '/auth/registration/',
  '/auth/token/refresh/',
  '/auth/google/',
  '/auth/facebook/',
];

// Single-flight refresh: concurrent 401s share one refresh request instead of
// each firing their own (which would invalidate each other under token rotation).
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refresh = tokenStorage.getRefresh();
      if (!refresh) throw new Error('No refresh token');
      const { data } = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, { refresh });
      tokenStorage.set(data.access, data.refresh);
      return data.access as string;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function redirectToLogin() {
  tokenStorage.clear();
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    const isAuthEndpoint = AUTH_PATHS.some((p) => originalRequest?.url?.includes(p));

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;
      try {
        const access = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return apiClient(originalRequest);
      } catch {
        redirectToLogin();
      }
    }
    return Promise.reject(error);
  }
);
